import {Button, DataStatus, openDialog} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isEmpty} from 'ramda';

import {CustomFieldPropertyResponseBody} from '@dms/api/core';
import {
  TenantFieldDefinitionResponseBody,
  useReorderFieldDefinitionsMutation,
} from '@dms/api/settings';
import {SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {CreateDefinitionForm} from '../(sections)/CreateDefinition/CreateDefinitionForm';
import {DefinitionCard} from './DefinitionCard';

interface ResourceDefinitionListProps extends RequiredTestIdProps {
  definitions: TenantFieldDefinitionResponseBody[];
  resourceId: string;
  customFieldPropertiesById: Record<string, CustomFieldPropertyResponseBody>;
}

const CONTAINER_ID = 'custom-fields-definitions';

export function ResourceDefinitionList(props: ResourceDefinitionListProps) {
  const [isCreating, startCreating, cancelCreating] = useBoolean(false);
  const [reorderDefinitions] = useReorderFieldDefinitionsMutation();

  const [canCreateDefinition] = usePermissions({permissionKeys: ['addCustomFieldsDefinitions']});

  const areDefinitionsEmpty = isEmpty(props.definitions);

  const handleCreateNewDefinition = () => {
    openDialog(
      <CreateDefinitionForm
        data-testid={testIds.settings.customFields(`addDefinitionForm-${props.resourceId}`)}
        resourceId={props.resourceId}
        onAfterSubmit={cancelCreating}
        areDefinitionsEmpty={areDefinitionsEmpty}
      />,
      {title: i18n.t('page.accounting.labels.newDefinition')}
    );
  };

  return (
    <DataStatus
      isEmpty={areDefinitionsEmpty && !isCreating}
      action={{
        title: i18n.t('page.generalSettings.labels.addField'),
        leftIcon: 'content/add_circle',
        onClick: startCreating,
      }}
    >
      <VStack spacing={4} align="flex-start">
        <SortableCardList
          withHandle
          disableDropAnimation
          containerId={CONTAINER_ID}
          items={props.definitions}
          onReorder={(reorderedItems) => {
            reorderDefinitions({
              items: reorderedItems.map((item) => item.id),
              resourceId: props.resourceId,
            })
              .unwrap()
              .catch(handleApiError);
          }}
          renderCard={(definition, index, handleProps) => (
            <DefinitionCard
              definition={definition}
              data-testid={suffixTestId(`[${index}]`, props)}
              customFieldProperty={props.customFieldPropertiesById[definition.id]}
              handleProps={handleProps}
            />
          )}
        />

        <Show when={canCreateDefinition}>
          <Button
            variant="link"
            leftIcon="content/add_circle"
            title={i18n.t('page.accounting.labels.newDefinition')}
            onClick={handleCreateNewDefinition}
            data-testid={suffixTestId('newDefinition', props)}
          />
        </Show>
      </VStack>
    </DataStatus>
  );
}
